import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import { setToken } from '../../../actions/authentication';
import { asyncActions } from '../../../actions/general';
import {
  afterAuthRoute,
  existingUserEventGA,
  loading,
  reestablishSession,
} from '../../../actions/sessions';
import { useAuth } from '../../../hooks/useAuth';
import {
  formatPhoneNumber,
  normalizePhone,
} from '../../../lib/utils/formatters';
import GA from '../../../lib/utils/ga';
import { emitEvent } from '../../../lib/utils/modal';
import Alert from '../../shared/Alert';
import Loading from '../../shared/Loading';
import Support from '../../shared/Support';
import UserTokenVerification from '../../shared/UserTokenVerification';

const ga = new GA();

export default function ExistingUserToken({ match }) {
  const { verify } = useAuth();
  const dispatch = useDispatch();
  const history = useHistory();
  const isLoading = useSelector((state) => state.sessions.get('loading'));
  const pathParts = match?.path.split('/');

  const resendCode = () => {
    dispatch(loading(true));
    return dispatch(asyncActions(verify('phone', match.params.contact, true)))
      .then(() => dispatch(loading(false)))
      .catch((err) => dispatch(loading(false)));
  };

  if (!match || (!match.params.type && !match.params.contact)) {
    return (
      <div id="content">
        <Alert
          containerClass="bg-red-50 p-4 mx-auto my-3"
          icon={['far', 'circle-xmark']}
          alertText={
            <p>
              Something went wrong, please
              <Link
                to="/init/existing_user"
                className="mx-1 underline cursor-pointer"
              >
                go back
              </Link>
              and try again.
            </p>
          }
        />
      </div>
    );
  }

  const afterAuthCallback = (twofactor, verifiedUser, verifiedToken) => {
    return dispatch(
      asyncActions(
        !twofactor && reestablishSession(verifiedUser, verifiedToken),
        pathParts[1] === 'init' && emitEvent(verifiedUser),
        pathParts[1] === 'init' && existingUserEventGA(ga, verifiedUser)
      )
    )
      .then(() => dispatch(afterAuthRoute(history, twofactor)))
      .finally(() => dispatch(loading(false)));
  };

  const onLockout = () => {
    history.push('/init/user_lockout');
  };

  const onNotBorrower = () => {
    history.push('/init/user_is_not_borrower');
  };

  const headerText = (
    <>
      {match.params.confirm
        ? 'We need to confirm your mobile phone'
        : `We just ${match.params.type === 'phone' ? 'texted' : 'emailed'} you`}
    </>
  );

  return (
    <div role="main" className="mx-6 sm:mx-auto">
      <div className="mt-4 mb-1 sm:mb-4">
        <div className="text-xl sm:text-4xl font-extrabold">{headerText}</div>
      </div>
      <div className="tracking-wide w-4/5 sm:w-full mb-2 sm:mb-6 font-light text-sm sm:text-lg break-words">
        Please enter the verification code we sent to
        <span className="text-primary-blue-hover block sm:inline sm:mx-1">
          {match.params.type === 'phone'
            ? formatPhoneNumber(normalizePhone(match.params.contact))
            : match.params.contact}
          .
        </span>
      </div>

      <div className="flex justify-start">
        <UserTokenVerification
          isLoading={isLoading}
          match={match}
          onAfterAuth={afterAuthCallback}
          onLoading={loading}
          onLockout={onLockout}
          onNotBorrower={onNotBorrower}
          onSaveToken={setToken}
        >
          <Loading className="w-14 h-14" />
        </UserTokenVerification>
      </div>

      {!match.params.confirm && (
        <>
          <div className="flex flex-col xxs:flex-row xxs:items-center w-full my-3 sm:mt-6">
            <p className="font-light text-sm sm:text-lg">
              Didn't receive the code?
            </p>
            <Link
              to="/init/existing_user"
              className="xxs:ml-1.5 text-sm sm:text-lg text-primary-blue hover:text-primary-blue-hover"
            >
              Change Number
            </Link>
          </div>
        </>
      )}

      {!match.params.confirm && match.params.type === 'phone' && (
        <div className="flex flex-col xxs:flex-row xxs:items-center w-full my-3 sm:mt-6">
          <p
            className="text-xs sm:text-sm text-secondary-gray underline cursor-pointer hover:text-primary-blue-hover"
            onClick={resendCode}
          >
            Send to my account email instead
          </p>
        </div>
      )}

      <div className="mt-6 sm:mt-12">
        <Support />
      </div>
    </div>
  );
}
